import type { EnemyKind } from '../types';

// The aesthetics seam. Everything the render layer needs to "look like
// something" lives here as data, so a future art pass (or per-level theming)
// is a config change, not a render-code rewrite. Themes progress the world
// visually day → dusk → night → deep night as difficulty ramps; the render
// layer swaps environments by reading `themeForLevel(level)` — no three.js
// types leak into this file (core stays pure).

export interface MaterialPreset {
  metalness: number;
  roughness: number;
  /** Glass-like presets enable transmission/clearcoat in the render layer. */
  glass: boolean;
  emissiveIntensity: number;
  /** 0-1 opacity for transparent presets. */
  opacity: number;
  /** How strongly the sky reflects in this material. Glass runs hot (>2) to
   *  counter ACES tone-mapping crush; the legacy look was tone-mapping-free. */
  envMapIntensity: number;
}

export interface GroundPreset {
  /** Diffuse map; null = flat tinted material. */
  textureUrl: string | null;
  /** Displacement map; null = only vertex noise. */
  displacementUrl: string | null;
  /** Material color multiplied over the texture. */
  tint: number;
  displacementScale: number;
  /** Texture tiling across the arena plane. */
  repeat: number;
  /** UV units/sec the ground drifts toward the player — the "marching through
   *  the world" illusion. 0 = static. Scaled up gently as levels rise. */
  scrollSpeed: number;
}

/** Sky/periphery life, per theme. Pure data — the render layer maps effect
 *  names to implementations (see platform/render/three/ambience.ts). */
export interface AmbiencePreset {
  /** Radians/sec the equirect sky slowly turns. */
  skyYawSpeed: number;
  effects: readonly ('clouds' | 'aurora' | 'shootingStars')[];
}

export interface LightPreset {
  ambient: number;
  ambientColor: number;
  sun: number;
  sunColor: number;
  sunPosition: [number, number, number];
  fill: number;
}

export interface Theme {
  id: string;
  label: string;
  /** Equirect sky image (JPEG); also used as the scene background. */
  environmentUrl: string | null;
  /** Fallback clear color while the sky streams in. */
  backgroundColor: number;
  /** Brightness boost for the sky backdrop — LDR (jpg) skies get crushed by
   *  ACES tone mapping, and dark starfields need extra punch. */
  backgroundIntensity: number;
  fog: { color: number; density: number } | null;
  /** Renderer toneMappingExposure. */
  exposure: number;
  lights: LightPreset;
  ground: GroundPreset;
  ambience: AmbiencePreset;
  materials: Record<EnemyKind, MaterialPreset>;
  projectileColor: number;
  playerColor: number;
}

// Shared material presets: the same metal/glass pair everywhere, but night
// themes bump emissiveIntensity so enemies glow in the dark instead of
// vanishing into it.
const DAY_MATERIALS: Record<EnemyKind, MaterialPreset> = {
  metal: { metalness: 0.5, roughness: 0.5, glass: false, emissiveIntensity: 0.2, opacity: 1, envMapIntensity: 1.0 },
  glass: { metalness: 0.0, roughness: 0.4, glass: true, emissiveIntensity: 0.2, opacity: 0.85, envMapIntensity: 2.2 },
};

const NIGHT_MATERIALS: Record<EnemyKind, MaterialPreset> = {
  metal: { metalness: 0.5, roughness: 0.5, glass: false, emissiveIntensity: 0.35, opacity: 1, envMapIntensity: 1.0 },
  glass: { metalness: 0.0, roughness: 0.4, glass: true, emissiveIntensity: 0.45, opacity: 0.85, envMapIntensity: 2.2 },
};

export const THEMES: Record<string, Theme> = {
  day: {
    id: 'day',
    label: 'High Noon',
    environmentUrl: '/env/day.jpg',
    backgroundColor: 0xbfd9e8,
    backgroundIntensity: 1.4,
    fog: { color: 0xcfe4ee, density: 0.008 },
    exposure: 1.0,
    lights: {
      ambient: 0.9,
      ambientColor: 0xffffff,
      sun: 1.6,
      sunColor: 0xfff4e0,
      sunPosition: [30, 50, 20],
      fill: 1.2,
    },
    ground: {
      textureUrl: '/textures/ground-sand.jpg',
      displacementUrl: '/textures/ground-sand-disp.png',
      tint: 0xffffff,
      displacementScale: 1.5,
      repeat: 6,
      scrollSpeed: 0.014,
    },
    ambience: { skyYawSpeed: 0.003, effects: ['clouds'] },
    materials: DAY_MATERIALS,
    projectileColor: 0x00ffff,
    playerColor: 0xffffff,
  },
  // The dusk sky asset is a moonlit overcast cloudscape, so the palette is
  // cool silver-blue rather than golden-hour orange.
  dusk: {
    id: 'dusk',
    label: 'Moonrise',
    environmentUrl: '/env/dusk.jpg',
    backgroundColor: 0x9aa3b2,
    backgroundIntensity: 1.5,
    fog: { color: 0x8b95a6, density: 0.010 },
    exposure: 1.0,
    lights: {
      ambient: 0.85,
      ambientColor: 0xc9d2e0,
      sun: 1.3,
      sunColor: 0xdfe8ff,
      sunPosition: [-30, 40, 10],
      fill: 1.0,
    },
    ground: {
      textureUrl: '/textures/ground-sand.jpg',
      displacementUrl: '/textures/ground-sand-disp.png',
      tint: 0xb0a89a,
      displacementScale: 1.5,
      repeat: 6,
      scrollSpeed: 0.017,
    },
    ambience: { skyYawSpeed: 0.004, effects: ['clouds', 'shootingStars'] },
    materials: DAY_MATERIALS,
    projectileColor: 0x00ffff,
    playerColor: 0xffffff,
  },
  night: {
    id: 'night',
    label: 'Nightfall',
    environmentUrl: '/env/night.jpg',
    backgroundColor: 0x0d1626,
    backgroundIntensity: 1.7,
    fog: { color: 0x1a2438, density: 0.011 },
    exposure: 0.95,
    lights: {
      ambient: 0.8,
      ambientColor: 0x99aadd,
      sun: 1.1,
      sunColor: 0xaabbff,
      sunPosition: [0, 50, -10],
      fill: 0.9,
    },
    ground: {
      textureUrl: '/textures/ground-rock.jpg',
      displacementUrl: '/textures/ground-rock-disp.png',
      tint: 0xaabbdd,
      displacementScale: 1.5,
      repeat: 6,
      scrollSpeed: 0.02,
    },
    ambience: { skyYawSpeed: 0.005, effects: ['shootingStars', 'aurora'] },
    materials: NIGHT_MATERIALS,
    projectileColor: 0x00ffff,
    playerColor: 0xffffff,
  },
  deepNight: {
    id: 'deepNight',
    label: 'Deep Night',
    environmentUrl: '/env/deep-night.jpg',
    backgroundColor: 0x05070d,
    backgroundIntensity: 2.2,
    fog: { color: 0x141a2e, density: 0.012 },
    exposure: 0.9,
    lights: {
      ambient: 0.7,
      ambientColor: 0x8899cc,
      sun: 0.95,
      sunColor: 0x99aaff,
      sunPosition: [10, 40, -20],
      fill: 0.8,
    },
    ground: {
      textureUrl: '/textures/ground-cliff.jpg',
      displacementUrl: '/textures/ground-cliff-disp.png',
      tint: 0x99aabb,
      displacementScale: 1.5,
      repeat: 6,
      scrollSpeed: 0.023,
    },
    ambience: { skyYawSpeed: 0.006, effects: ['shootingStars', 'aurora'] },
    materials: NIGHT_MATERIALS,
    projectileColor: 0x00ffff,
    playerColor: 0xffffff,
  },
};

// The difficulty → atmosphere mapping: two levels per phase, then the game
// stays in deep night for the endgame grind.
export function themeForLevel(level: number): Theme {
  if (level <= 2) return THEMES.day;
  if (level <= 4) return THEMES.dusk;
  if (level <= 6) return THEMES.night;
  return THEMES.deepNight;
}

export const DEFAULT_THEME = THEMES.day;
